/** A floating window: dragged by its title bar, resized by its corner, raised on click. */
import { Show, createMemo, type JSX } from "solid-js";
import { useApp } from "../app/context";
import * as L from "./layout";
import type { AreaContext } from "./areaContext";
import { WindowBody } from "./WindowBody";

/** A window drawn above the tiles. */
export function FloatingView(props: { window: string; ctx: AreaContext }): JSX.Element {
  const { wm } = useApp();
  const rect = createMemo(() => wm.layout().floating.find((f) => f.window === props.window));
  const depth = createMemo(() => wm.layout().floating.findIndex((f) => f.window === props.window));

  /** Drags the bottom-right corner to resize the window. */
  function resizeFrom(event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const start = rect();
    if (!start) return;
    const [x0, y0] = [event.clientX, event.clientY];
    const move = (ev: PointerEvent) =>
      wm.update((l) => L.moveFloating(l, props.window, { width: Math.max(240, start.width + ev.clientX - x0), height: Math.max(160, start.height + ev.clientY - y0) }));
    const up = () => {
      removeEventListener("pointermove", move);
      removeEventListener("pointerup", up);
    };
    addEventListener("pointermove", move);
    addEventListener("pointerup", up);
  }

  return (
    <Show when={rect()}>
      {(r) => (
        <section
          class="win floating"
          data-floating={props.window}
          style={{ left: `${r().x}px`, top: `${r().y}px`, width: `${r().width}px`, height: `${r().height}px`, "z-index": 10 + depth() }}
          onPointerDown={() => wm.update((l) => L.raise(l, props.window))}
        >
          <header class="tabbar title" onPointerDown={(e) => props.ctx.beginDrag(e, props.window, "floating")}>
            <span class="tab active">
              <span class="tab-label">{wm.def(props.window)?.title ?? props.window}</span>
            </span>
            <span class="sp" />
            <button type="button" class="win-btn" title="Dock this window" aria-label="Dock window" data-dock onPointerDown={(e) => e.stopPropagation()} onClick={() => wm.update((l) => L.open(L.detach(l, props.window), props.window))}>
              ⇲
            </button>
            <button type="button" class="win-btn" title="Close window" aria-label="Close window" onPointerDown={(e) => e.stopPropagation()} onClick={() => wm.close(props.window)}>
              ×
            </button>
          </header>
          <WindowBody window={props.window} />
          <div class="resize-corner" onPointerDown={resizeFrom} />
        </section>
      )}
    </Show>
  );
}
